import LoadingSpinner from '@/components/LoadingSpinner';
import { accessibilityDataAtom } from '@/global-store';
import { useFetchUrlMap } from '@http/queries';
import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IssueExportForm from '../IssueExportForm';
import {
    buildBulletListAdf,
    buildJiraAdfTable,
    buildLinkAdf,
    formattedText,
    headingAdf,
    subHeadingWithTextAdf,
} from './adfUtils';

export function IssuesExport({
    todosToExport,
    jiraProject,
    jiraUsers,
    linkedJiraUserID,
    issueExportFormRef,
}) {
    const { t } = useTranslation();

    const { data: urlMap, isLoading: urlMapIsLoading, isError: urlMapIsError } = useFetchUrlMap();

    const [accessibilityData] = useAtom(accessibilityDataAtom);

    const ruleId = todosToExport[0].props.ruleNum;

    const conformanceTarget = accessibilityData?.conformanceTarget.split(' + ');
    let impactconformanceTarget = `diff${conformanceTarget[0]}`;
    if (conformanceTarget[1]) impactconformanceTarget += `_BP`;

    const eaRule = accessibilityData?.rules[ruleId]?.eaRule;
    const impactScore = accessibilityData.auditScores?.ruleImpacts
        ? accessibilityData.auditScores.ruleImpacts?.[ruleId]?.[impactconformanceTarget]
        : undefined;

    const issuesTableRows = useMemo(() => {
        if (urlMapIsError || urlMapIsLoading) return [];

        const data = [];

        const rule = accessibilityData.rules[ruleId];

        const summaryPerPage = accessibilityData.report.summary.summaryPerPage;

        for (const [url, pageObj] of Object.entries(rule.perPage)) {
            const occurrences = pageObj.totalErrors;

            if (occurrences === 0) continue;

            const title = summaryPerPage[url].title || t('noPageTitle');

            const pageIndex = Object.keys(urlMap).find(key => urlMap[key] === url);

            const pageUrlAdf = buildLinkAdf(title, url);

            data.push([pageUrlAdf, occurrences.toString()]);
        }

        return data;
    }, [accessibilityData, ruleId, urlMap, urlMapIsError, buildLinkAdf, urlMapIsLoading, t]);

    if (!jiraUsers) return <LoadingSpinner />;

    const headers = [t('titleUrl'), t('occurrences')];

    const jiraBodyDescripiton = [];

    // Issue Title
    if (eaRule.content) jiraBodyDescripiton.push(headingAdf(eaRule.content, 2));

    // Issue Description
    jiraBodyDescripiton.push(formattedText(t('issueDetailSubHead1'), 'strong'));
    jiraBodyDescripiton.push(formattedText(eaRule.explanation));

    // Issue How to fix
    jiraBodyDescripiton.push(formattedText(t('issueDetailSubHead2'), 'strong'));
    jiraBodyDescripiton.push(formattedText(eaRule.tip));

    // Issue properties

    jiraBodyDescripiton.push(formattedText(t('issueDetailSubHead'), 'strong'));
    const categories = accessibilityData?.eaRules?.[ruleId].categories.map(cat => {
        const level = accessibilityData.wcagRules[cat].wcagLvl;
        const formatedLevel = level === 'Best Practice' ? level : 'Level ' + level;
        return cat === 'Best Practice'
            ? `${formatedLevel} - ${accessibilityData.wcagRules[cat].name}`
            : {
                  type: 'text',
                  text: `${formatedLevel} - ${cat} ${accessibilityData.wcagRules[cat].name}`,
                  marks: [
                      {
                          type: 'link',
                          attrs: {
                              href: accessibilityData.wcagRules[cat].urlWCAG,
                          },
                      },
                  ],
              };
    });

    jiraBodyDescripiton.push(buildBulletListAdf(categories));

    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(t('complexity'), t('issueComplexity+' + eaRule.complexity))
    );
    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(
            t('responsibility'),
            t('issueResponsibility+' + eaRule.responsibility)
        )
    );
    if (impactScore)
        jiraBodyDescripiton.push(
            subHeadingWithTextAdf(t('issuesImpactScore'), impactScore.toString())
        );

    // pages with this issue's table
    if (issuesTableRows.length > 0) {
        const tableAdf = buildJiraAdfTable(headers, issuesTableRows);
        if (JSON.stringify(tableAdf).length < 25000) {
            jiraBodyDescripiton.push(headingAdf(t('issueDetailTableHeader'), 3));
            jiraBodyDescripiton.push(tableAdf);
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <IssueExportForm
                ref={issueExportFormRef}
                todosToExport={todosToExport}
                jiraProject={jiraProject}
                jiraBodyDescripiton={jiraBodyDescripiton}
                jiraUsers={jiraUsers}
                linkedJiraUserID={linkedJiraUserID}
            />
        </Box>
    );
}
